import _IconBranch from "@arco-design/web-react/icon/react-icon/IconBranch";
import "@arco-design/web-react/es/Select/style";
import _Select from "@arco-design/web-react/es/Select";
import _IconFolder from "@arco-design/web-react/icon/react-icon/IconFolder";
import _IconDown from "@arco-design/web-react/icon/react-icon/IconDown";
import _IconArchive from "@arco-design/web-react/icon/react-icon/IconArchive";
import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import cs from 'classnames';
import { resolveRouteUrl, useActiveRoute, useResolvedRoutes, useRouteParams, useRouteUrl } from '@/routes';
import useLocale from '@/utils/useLocale';
import { ApiGetProjectList, ApiGetTenantList, ApiSwitchProject } from '@/api';
import styles from './style/index.module.less';

/**
 * 作用域切换入口（NavBar）。
 * 入口目标全部由路由配置推导，NavBar 不硬编码任何业务路径：
 *   - Project -> 当前团队下的项目列表，选中即切换（写入会话）并进入 /project 作用域
 *   - Tenant  -> 当前项目下的租户，选中即进入 /tenant/:tenantId 作用域
 *
 * 项目切换需要后端把 project_id 写回会话 Token，因此切换成功后整页重载，
 * 保证所有列表按新项目重新拉取。
 * 团队切换仍保留在右上角用户菜单中。
 */
export default function ScopeSwitcher() {
  const history = useHistory();
  const locale = useLocale();
  const routes = useResolvedRoutes();
  const match = useActiveRoute();
  const params = useRouteParams();
  const userInfo = useSelector(state => state.userInfo);
  const [projects, setProjects] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [projectPopupVisible, setProjectPopupVisible] = useState(false); // 当前团队下的项目

  const fetchProjects = useCallback(() => {
    ApiGetProjectList({
      team_id: userInfo?.team_id
    }).then(res => {
      const payload = res.data.data || {};
      setProjects(payload.list || []);
    }).catch(() => {
      setProjects([]);
    });
  }, [userInfo?.team_id]);
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]); // 当前项目下的租户

  useEffect(() => {
    ApiGetTenantList({
      limit: 1000,
      offset: 0
    }).then(req => {
      const payload = req.data.data || {};
      setTenants(payload.list || []);
    }).catch(() => {
      setTenants([]);
    });
  }, [userInfo?.project_id]);
  const currentProjectId = userInfo?.project_id;
  const currentProjectName = useMemo(() => {
    const matched = projects.find(project => project.id === currentProjectId);
    return matched?.name || userInfo?.project_name || '';
  }, [projects, currentProjectId, userInfo?.project_name]);
  const projectOptions = useMemo(() => projects.map(project => ({
    label: project.name,
    value: project.id
  })), [projects]);
  const tenantOptions = useMemo(() => tenants.map(tenant => ({
    label: tenant.name,
    value: tenant.id
  })), [tenants]); // 当前处于哪个作用域：'project' | 'tenant' | undefined(根作用域)

  const activeScope = match?.route.scope?.key;
  const projectUrl = useRouteUrl('project');

  function goTo(url) {
    if (url && url !== '#') {
      history.push(url);
    }
  }

  function enterProject() {
    setProjectPopupVisible(false);
    goTo(projectUrl);
  }

  function onSelectTenant(value) {
    if (!value) {
      return;
    }

    goTo(resolveRouteUrl(routes, 'tenant', {
      tenantId: `${value}`
    }));
  } // 选择团队下的项目: 命中当前项目直接进入, 否则先写会话再整页重载


  function onSelectProject(value) {
    const projectId = `${value}`;

    if (switching) {
      return;
    }

    if (projectId === currentProjectId) {
      enterProject();
      return;
    }

    setSwitching(true);
    ApiSwitchProject({
      project_id: projectId
    }).then(res => {
      if (res.data.code !== 0) {
        _Message.error(res.data.message || locale['nav.scope.project.switchFailed'] || 'Switch project failed');

        setSwitching(false);
        return;
      } // 会话中的 project_id 已更新, 重新加载以按新项目拉取数据


      window.location.href = projectUrl && projectUrl !== '#' ? projectUrl : '/';
    }).catch(() => {
      _Message.error(locale['nav.scope.project.switchFailed'] || 'Switch project failed');

      setSwitching(false);
    });
  }

  return /*#__PURE__*/React.createElement("div", {
    className: styles.scope
  }, /*#__PURE__*/React.createElement(_Select, {
    className: cs(styles['scope-select'], {
      [styles['scope-select-active']]: activeScope === 'project'
    }),
    value: currentProjectId || undefined,
    options: projectOptions,
    onChange: onSelectProject,
    bordered: false,
    loading: switching,
    placeholder: locale['nav.scope.project'] || 'Project',
    prefix: /*#__PURE__*/React.createElement(_IconArchive, null),
    suffixIcon: /*#__PURE__*/React.createElement(_IconDown, null),
    notFoundContent: locale['nav.scope.project.empty'] || 'No project available',
    renderFormat: () => currentProjectName,
    popupVisible: projectPopupVisible,
    onVisibleChange: visible => {
      setProjectPopupVisible(visible); // 打开下拉时重新拉取: 团队管理页可能刚增删改名过项目

      if (visible) {
        fetchProjects();
      }
    },
    triggerProps: {
      autoAlignPopupWidth: false,
      position: 'bl'
    },
    dropdownMenuStyle: {
      minWidth: 200
    },
    dropdownRender: menu => /*#__PURE__*/React.createElement(React.Fragment, null, menu, /*#__PURE__*/React.createElement("div", {
      className: styles['scope-select-extra'],
      onClick: enterProject
    }, /*#__PURE__*/React.createElement(_IconFolder, null), locale['nav.scope.project.enter'] || 'Enter project'))
  }), /*#__PURE__*/React.createElement(_Select, {
    className: cs(styles['scope-select'], {
      [styles['scope-select-active']]: activeScope === 'tenant'
    }),
    value: params.tenantId,
    options: tenantOptions,
    onChange: onSelectTenant,
    bordered: false,
    autoWidth: {
      minWidth: 150,
      maxWidth: 500
    } // 当前项目下没有租户时不可切
    // 换
    ,
    disabled: !tenants.length,
    placeholder: locale['nav.scope.tenant'] || 'Tenant',
    prefix: /*#__PURE__*/React.createElement(_IconBranch, null),
    triggerProps: {
      autoAlignPopupWidth: false,
      position: 'bl'
    },
    dropdownMenuStyle: {
      minWidth: 200
    }
  }));
}